
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  generateReferralUrl,
  getBadgeLevel, 
  fetchReferralCounts, 
  syncReferrerNames,
  updateInviteCount
} from '@/utils/referralUtils';
import { useAuth } from '@/context/AuthContext';
import { Share2, QrCode, Users } from 'lucide-react';
import { toast } from 'sonner';
import QRModal from './QRModal';
import { supabase } from '@/integrations/supabase/client';
import { ReferralBadges } from './referral/ReferralBadges';

const ReferralManager = () => {
  const { user } = useAuth();
  const [referralUrl, setReferralUrl] = useState<string>('');
  const [showQrModal, setShowQrModal] = useState(false);
  const [referralCount, setReferralCount] = useState(0);
  const [pendingInvites, setPendingInvites] = useState(0);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (user) {
      // Make sure we're using the absolute URL that works in production
      const url = generateReferralUrl(user.id);
      console.log("Generated referral URL:", url); // Debug log
      setReferralUrl(url);
      fetchReferralData();
      
      // Sync any missing referrer names on initial load
      syncReferrerNames().then(success => {
        if (success) {
          console.log('Referrer names synced successfully');
        }
      });
      
      // Subscribe to real-time updates for the community_members table
      const channel = supabase
        .channel('public:community_members')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'community_members',
            filter: `referred_by=eq.${user.id}`
          },
          (payload) => {
            console.log('Referral change detected:', payload);
            fetchReferralData();
            
            // Show a toast notification if the count increased
            if (payload.eventType === 'UPDATE' && 
                payload.new?.referred_by === user.id && 
                payload.new?.approved === true && 
                payload.old?.approved === false) {
              toast.success('A member you referred has been approved!');
            }
          }
        )
        .subscribe();
      
      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user]);
  
  const fetchReferralData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Get both approved and pending referral counts
      const counts = await fetchReferralCounts(user.id);
      
      // Get invite_count from the user's profile
      const { data: userData, error: userError } = await supabase
        .from('community_members')
        .select('invite_count, referral_code')
        .eq('id', user.id)
        .single();
      
      if (userError) throw userError;
      
      // If the user doesn't have an invite_count or it's different from the actual approved count,
      // update it to match the actual count
      if (userData && (userData.invite_count === null || userData.invite_count !== counts.approved)) {
        // Update the invite_count to match the actual number of approved referrals
        await updateInviteCount(user.id);
      }
      
      // If the user doesn't have a referral code, generate one
      if (userData && !userData.referral_code) {
        console.log("User is missing a referral code, this should be fixed by the trigger");
      }
      
      setReferralCount(userData?.invite_count || counts.approved);
      setPendingInvites(counts.pending);
    } catch (error) {
      console.error('Error fetching referral data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralUrl)
      .then(() => {
        toast.success("Referral link copied to clipboard!");
        console.log("Copied referral URL:", referralUrl);
      })
      .catch(() => toast.error("Failed to copy link"));
  };
  
  const badge = getBadgeLevel(referralCount);
  
  const getBadgeColor = (level: string) => {
    switch (level) {
      case 'bronze': return 'bg-amber-600 hover:bg-amber-700';
      case 'silver': return 'bg-gray-400 hover:bg-gray-500';
      case 'gold': return 'bg-yellow-500 hover:bg-yellow-600';
      case 'platinum': return 'bg-cyan-500 hover:bg-cyan-600';
      case 'legend': return 'bg-purple-600 hover:bg-purple-700';
      default: return 'bg-gray-300 hover:bg-gray-400';
    }
  };
  
  if (loading) {
    return (
      <Card className="border-omoduduwa-terracotta/30">
        <CardContent className="p-6 flex justify-center items-center">
          <div className="animate-spin h-8 w-8 border-4 border-omoduduwa-terracotta border-t-transparent rounded-full"></div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <>
      <Card className="border-omoduduwa-terracotta/30">
        <CardHeader className="bg-gradient-to-r from-omoduduwa-terracotta/20 to-transparent">
          <CardTitle className="flex items-center">
            <Users className="mr-2 text-omoduduwa-terracotta" /> Your Referral Dashboard
          </CardTitle>
          <CardDescription>Invite others and track your referrals</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:justify-between gap-4 items-center bg-gray-50 p-4 rounded-lg">
            <div className="text-center sm:text-left">
              <div className="text-sm text-gray-500 mb-1">Your current badge</div>
              <div className="flex items-center justify-center sm:justify-start gap-2">
                <Badge className={`text-white ${getBadgeColor(badge.level)}`}>
                  {badge.label} Inviter
                </Badge>
                <span className="text-sm">
                  ({referralCount} {referralCount === 1 ? 'invite' : 'invites'})
                </span>
              </div>
              
              {pendingInvites > 0 && (
                <div className="mt-2 text-sm text-amber-600">
                  + {pendingInvites} pending {pendingInvites === 1 ? 'invite' : 'invites'} awaiting approval
                </div>
              )}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2">
              <Button 
                variant="outline" 
                className="flex items-center gap-2"
                onClick={() => setShowQrModal(true)}
              >
                <QrCode size={16} />
                <span>QR Code</span>
              </Button>
              
              <Button 
                className="bg-omoduduwa-terracotta hover:bg-omoduduwa-terracotta/90 flex items-center gap-2"
                onClick={handleCopyLink}
              >
                <Share2 size={16} />
                <span>Copy Link</span>
              </Button>
            </div>
          </div>
          
          <ReferralBadges />
          
          <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-md">
            <p className="text-sm text-yellow-800">
              <strong>Why Invite Others?</strong> Help grow our community and earn recognition with exclusive badges. The more people you invite, the higher your ranking!
            </p>
          </div>
        </CardContent>
      </Card>
      
      <QRModal
        isOpen={showQrModal}
        onClose={() => setShowQrModal(false)}
        url={referralUrl}
        title="Your Personal QR Code"
        description="Share this QR code to invite others to join the Yoruba Progressive League. When they scan and register, you'll get credit for the referral."
      />
    </>
  );
};

export default ReferralManager;
